import { walkingPath, type Coordinates3D, type FloorPoint, type PickingRouteTask, type PickRoute, type WarehouseMap } from '@wms/domain';
import { useCallback, useMemo, useState } from 'react';
import { Icon } from '../icons';
import { SellerChip } from '../index';
import { buildWorld, groupPins, shelfFace, type Cell, type P2, type PinGroup } from './geometry';
import { MapView, type CellPaint, type MapHit, type MapPath } from './MapView';

type StopState = 'done' | 'pending' | 'active' | 'short';
interface Stop {
  readonly task: PickingRouteTask;
  readonly state: StopState;
}

const RANK: Record<StopState, number> = { done: 0, pending: 1, short: 2, active: 3 };
const STATE_LABEL: Record<StopState, string> = { done: 'Toplandı', pending: 'Bekliyor', active: 'Şimdiki hedef', short: 'Eksik' };
const COLOR: Record<StopState, string> = { done: 'var(--map-done)', pending: 'var(--map-pending)', active: 'var(--map-active)', short: 'var(--map-short)' };
/** badge diameter, pixels */
const PIN = 22;

const floor = (c: Coordinates3D): Coordinates3D => ({ x: c.x, y: c.y, z: 0 });

/** Rounded bubble around grouped badges with a tail down to their shelf. */
function bubblePath(group: PinGroup<Stop>): string {
  const { x, y, w, h } = group.box;
  const r = Math.min(h / 2, PIN / 2 + 3);
  const tip = { x: group.anchor.x, y: group.anchor.y - 2 };
  const half = Math.min(6, w / 2 - r);
  return [
    `M${x + r},${y}`,
    `H${x + w - r}`,
    `A${r},${r} 0 0 1 ${x + w},${y + r}`,
    `V${y + h - r}`,
    `A${r},${r} 0 0 1 ${x + w - r},${y + h}`,
    `H${tip.x + half}`,
    `L${tip.x},${tip.y}`,
    `L${tip.x - half},${y + h}`,
    `H${x + r}`,
    `A${r},${r} 0 0 1 ${x},${y + h - r}`,
    `V${y + r}`,
    `A${r},${r} 0 0 1 ${x + r},${y}`,
    'Z',
  ].join(' ');
}

function StopGroup({ group }: { group: PinGroup<Stop> }) {
  const r = PIN / 2;
  const many = group.pins.length > 1;
  const title = group.pins
    .map((p) => `${p.item.task.sequence}. ${p.item.task.location.code} · ${p.item.task.product.name} · ${p.item.task.quantity} adet · ${STATE_LABEL[p.item.state]}`)
    .join('\n');
  return (
    <g className={`map-pin${many ? ' many' : ''}`} data-hit={`pin:${group.id}`}>
      <title>{title}</title>
      {many && (
        <>
          <path d={bubblePath(group)} className="map-bubble" />
          {/* each stop's own shelf, when the group spans more than one */}
          {group.pins.map((p) => (
            <circle key={`at-${p.id}`} cx={p.at.x} cy={p.at.y} r={3.2} className="map-pin-dot" style={{ fill: COLOR[p.item.state] }} />
          ))}
        </>
      )}
      {group.pins.map((p, i) => {
        const at = group.slots[i]!;
        const active = p.item.state === 'active';
        return (
          <g key={p.id} className={`map-stop ${p.item.state}`}>
            {active && <circle cx={at.x} cy={at.y} r={r + 5} className="map-ring" />}
            <circle cx={at.x} cy={at.y} r={active && !many ? r + 2 : r} style={{ fill: COLOR[p.item.state] }} />
            <text x={at.x} y={at.y + 4}>{p.item.task.sequence}</text>
          </g>
        );
      })}
    </g>
  );
}

/**
 * Warehouse map of a pick route: racks and shelves, the walking path the route takes (done
 * legs green, the next one marching), every stop numbered, the next bin pulsing, where the
 * picker stands. Stops on the same shelf share a bubble so every number stays visible; a tap
 * lists them.
 */
export function RouteMap({ map, route }: { map: WarehouseMap; route: PickRoute }) {
  const world = useMemo(() => buildWorld(map), [map]);
  const stops = useMemo((): Stop[] => {
    const active = route.tasks.find((t) => t.status === 'pending')?.sequence ?? null;
    return route.tasks.map((task) => ({
      task,
      state: task.status === 'picked' ? 'done' : task.status === 'short' ? 'short' : task.sequence === active ? 'active' : 'pending',
    }));
  }, [route]);
  const located = useMemo(() => stops.filter((s) => s.task.location.coordinates), [stops]);
  const anchorOf = useCallback((s: Stop) => shelfFace(world, s.task.location.code, s.task.location.coordinates!), [world]);

  // the strongest state on each bin colours its shelf
  const byCode = useMemo(() => {
    const states = new Map<string, StopState>();
    for (const s of stops) {
      const current = states.get(s.task.location.code);
      if (!current || RANK[s.state] > RANK[current]) states.set(s.task.location.code, s.state);
    }
    return states;
  }, [stops]);
  const paint = useCallback(
    (cell: Cell): CellPaint | undefined => {
      let best: StopState | undefined;
      for (const code of cell.codes) {
        const s = byCode.get(code);
        if (s && (!best || RANK[s] > RANK[best])) best = s;
      }
      if (!best) return undefined;
      return { fill: COLOR[best], className: best === 'active' ? 'map-active' : undefined, title: `${cell.label} · ${STATE_LABEL[best]}` };
    },
    [byCode],
  );

  const start: Coordinates3D | null = route.start ?? map.layout.depot ?? null;
  const legs = useMemo((): MapPath[] => {
    const out: MapPath[] = [];
    let previous: FloorPoint | null = start ? { aisle: null, at: floor(start) } : null;
    for (const s of located) {
      const here: FloorPoint = { aisle: s.task.location.aisle, at: floor(s.task.location.coordinates!) };
      if (previous) {
        const className = s.state === 'done' || s.state === 'short' ? 'done' : s.state === 'active' ? 'todo next' : 'todo';
        out.push({ points: walkingPath(previous, here, map.layout, world.cross), className });
      }
      previous = here;
    }
    if (start && previous && previous.aisle !== null) out.push({ points: walkingPath(previous, { aisle: null, at: floor(start) }, map.layout, world.cross), className: 'back' });
    return out;
  }, [located, start, map.layout, world.cross]);

  const active = located.find((s) => s.state === 'active') ?? null;
  const focus = useMemo(() => (active ? anchorOf(active) : null), [active, anchorOf]);
  const routePoints = useMemo(() => [...located.map(anchorOf), ...(start ? [floor(start)] : [])], [located, anchorOf, start]);
  const lastDone = [...located].reverse().find((s) => s.state === 'done' || s.state === 'short');
  const here = lastDone ? floor(lastDone.task.location.coordinates!) : start ? floor(start) : null;

  // stops listed in the card under the map after a tap
  const [picked, setPicked] = useState<readonly number[] | null>(null);
  const onTap = (hit: MapHit | null) => {
    if (hit?.kind === 'pin') return setPicked(hit.id.split(',').map(Number));
    if (hit?.kind === 'cell') {
      const onShelf = stops.filter((s) => hit.cell.codes.includes(s.task.location.code)).map((s) => s.task.sequence);
      return setPicked(onShelf.length ? onShelf : null);
    }
    setPicked(null);
  };
  const shown = picked ? stops.filter((s) => picked.includes(s.task.sequence)) : [];

  const overlay = (at: (p: Coordinates3D) => P2) => {
    const groups = groupPins(
      located.map((s) => ({ id: String(s.task.sequence), at: at(anchorOf(s)), item: s })),
      PIN,
    );
    // the group with the current target is drawn last, over its neighbours
    const hasActive = (g: PinGroup<Stop>) => (g.pins.some((p) => p.item.state === 'active') ? 1 : 0);
    groups.sort((a, b) => hasActive(a) - hasActive(b));
    const hereAt = here ? at(here) : null;
    return (
      <>
        {hereAt && (
          <g className="map-here">
            <circle cx={hereAt.x} cy={hereAt.y} r={6} />
            <text x={hereAt.x} y={hereAt.y - 11}>
              Buradasınız
            </text>
          </g>
        )}
        {groups.map((g) => (
          <StopGroup key={g.id} group={g} />
        ))}
      </>
    );
  };

  const card =
    shown.length > 0 ? (
      <div className="map-pop" role="dialog" aria-label="Duraklar">
        <div className="map-pop-h">
          <b>{[...new Set(shown.map((s) => s.task.location.code))].join(' · ')}</b>
          <span className="muted">{shown.length > 1 ? `${shown.length} durak aynı yerde` : 'Durak'}</span>
          <button className="btn sm ghost" onClick={() => setPicked(null)} aria-label="Kapat">
            <Icon name="x" size={16} />
          </button>
        </div>
        <ul>
          {shown.map((s) => (
            <li key={s.task.sequence}>
              <span className="map-pop-n" style={{ background: COLOR[s.state] }}>
                {s.task.sequence}
              </span>
              <div className="what">
                <div className="name">{s.task.product.name}</div>
                <div className="muted">
                  <SellerChip code={s.task.product.tenantCode} /> <span className="mono">{s.task.location.code}</span> · {STATE_LABEL[s.state]}
                </div>
              </div>
              <b className="qty">{s.task.quantity} adet</b>
            </li>
          ))}
        </ul>
      </div>
    ) : null;

  return (
    <MapView
      world={world}
      label="Depo haritası ve toplama rotası"
      paint={paint}
      paths={legs}
      overlay={overlay}
      depot={start}
      frame={routePoints}
      frameKey={route.id}
      frames={[{ label: 'Rota', title: 'Rotanın tamamını göster', points: routePoints }]}
      focus={focus}
      onTap={onTap}
      card={card}
      legend={
        <>
          <span>
            <i style={{ background: 'var(--map-active)' }} /> Hedef
          </span>
          <span>
            <i style={{ background: 'var(--map-pending)' }} /> Bekleyen
          </span>
          <span>
            <i style={{ background: 'var(--map-done)' }} /> Toplandı
          </span>
          <span>
            <i style={{ background: 'var(--map-short)' }} /> Eksik
          </span>
          <span>
            <i className="line" /> Rota
          </span>
        </>
      }
    />
  );
}
